import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditRoomModal = ({ isOpen, onClose, onSuccess, room, token }) => {
  const [formData, setFormData] = useState({
    room_number: '',
    floor: '',
    room_type: 'Single',
    total_beds: '',
    rent_per_bed: '',
    deposit_amount: '',
    amenities: [],
    status: 'available',
    token_amount: '',
    maintenance_until: ''
  });
  const [loading, setLoading] = useState(false);
  const [amenityInput, setAmenityInput] = useState('');

  useEffect(() => {
    if (isOpen && room) {
      setFormData({
        room_number: room.room_number || '',
        floor: room.floor || '',
        room_type: room.room_type || 'Single',
        total_beds: room.total_beds?.toString() || '',
        rent_per_bed: (room.rent_per_bed ?? room.rent_amount ?? '').toString(),
        deposit_amount: (room.deposit_amount ?? '').toString(),
        amenities: room.amenities || [],
        status: room.status || 'available',
        token_amount: room.token_amount?.toString() || '',
        maintenance_until: (room.maintenance_until || '').slice(0, 10)
      });
    }
  }, [isOpen, room]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        property_id: room.property_id,
        total_beds: parseInt(formData.total_beds),
        rent_per_bed: parseFloat(formData.rent_per_bed) || 0,
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        token_amount: formData.status === 'booked' && formData.token_amount ? parseFloat(formData.token_amount) : null,
        maintenance_until: formData.status === 'under_maintenance' && formData.maintenance_until ? formData.maintenance_until : null
      };


      await axios.put(`${API}/properties/${room.property_id}/rooms/${room.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });


      toast({
        title: "Room Updated!",
        description: "Room details have been updated successfully"
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Room update error - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract error message from different error formats
      let errorMessage = "Failed to update room";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle string detail
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle validation error array
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message).join(', ');
        }
        // Handle validation error object
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || errorMessage;
        }
        // Handle direct message
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('Displaying error message:', errorMessage);
      
      toast({
        title: "Error Updating Room",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const addAmenity = () => {
    if (amenityInput.trim() && !formData.amenities.includes(amenityInput.trim())) {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, amenityInput.trim()]
      });
      setAmenityInput('');
    }
  };

  const removeAmenity = (amenity) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter(a => a !== amenity)
    });
  };

  if (!room) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Room</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Room Number *</Label>
              <Input
                value={formData.room_number}
                onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                placeholder="101"
                required
              />
            </div>

            <div>
              <Label>Floor *</Label>
              <Input
                value={formData.floor}
                onChange={(e) => setFormData({...formData, floor: e.target.value})}
                placeholder="Ground, First, Second"
                required
              />
            </div>

            <div>
              <Label>Room Type *</Label>
              <select
                value={formData.room_type}
                onChange={(e) => setFormData({...formData, room_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Single">Single</option>
                <option value="Double">Double</option>
                <option value="Triple">Triple</option>
                <option value="Dormitory">Dormitory</option>
              </select>
            </div>

            <div>
              <Label>Total Beds *</Label>
              <Input
                type="number"
                value={formData.total_beds}
                onChange={(e) => setFormData({...formData, total_beds: e.target.value})}
                placeholder="2"
                required
                min="1"
              />
            </div>

            <div>
              <Label>Rent per Bed (₹) *</Label>
              <Input
                type="number"
                value={formData.rent_per_bed}
                onChange={(e) => setFormData({...formData, rent_per_bed: e.target.value})}
                placeholder="12000"
                required
                min="0"
                step="100"
              />
              <p className="text-xs text-gray-500 mt-1">Monthly rent charged per bed in this room</p>
            </div>

            <div>
              <Label>Security Deposit (₹)</Label>
              <Input
                type="number"
                value={formData.deposit_amount}
                onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                placeholder="24000"
                min="0"
                step="100"
              />
              <p className="text-xs text-gray-500 mt-1">Refundable deposit per tenant</p>
            </div>

            <div>
              <Label>Status *</Label>
              <select
                value={formData.status}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    status: next,
                    // Reset fields that don't apply to the chosen status
                    token_amount: next === 'booked' ? prev.token_amount : '',
                    maintenance_until: next === 'under_maintenance' ? prev.maintenance_until : '',
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="booked">Booked</option>
                <option value="under_maintenance">Under Maintenance</option>
              </select>
            </div>

            {/* Token Amount - only show when status is "booked" */}
            {formData.status === 'booked' && (
              <div>
                <Label>Token Amount *</Label>
                <Input
                  type="number"
                  value={formData.token_amount}
                  onChange={(e) => setFormData({...formData, token_amount: e.target.value})}
                  placeholder="5000"
                  required={formData.status === 'booked'}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Advance/booking amount received</p>
              </div>
            )}

            {/* Maintenance Until - only show when status is "under_maintenance" */}
            {formData.status === 'under_maintenance' && (
              <div>
                <Label>Under Maintenance Until</Label>
                <Input
                  type="date"
                  value={formData.maintenance_until}
                  onChange={(e) => setFormData({...formData, maintenance_until: e.target.value})}
                  min={new Date().toISOString().slice(0, 10)}
                />
                <p className="text-xs text-gray-500 mt-1">Room will auto-flip to Available on this date. Leave blank for open-ended maintenance.</p>
              </div>
            )}

            <div className="col-span-2">
              <Label>Amenities</Label>
              <div className="flex gap-2">
                <Input
                  value={amenityInput}
                  onChange={(e) => setAmenityInput(e.target.value)}
                  placeholder="Add amenity (e.g., WiFi, AC)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAmenity();
                    }
                  }}
                />
                <Button type="button" onClick={addAmenity} variant="outline">
                  Add
                </Button>
              </div>
              {formData.amenities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      {amenity}
                      <button
                        type="button"
                        onClick={() => removeAmenity(amenity)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Updating...' : 'Update Room'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoomModal;
